import { useState } from "react";

import { Grid } from "@mui/material";
import { Cached, Download, Upload, Visibility } from "@mui/icons-material";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import { format } from "date-fns";
import { es, enUS } from "date-fns/locale";

import MDBox from "@vendor/components/MDBox";
import MenuCard from "@vendor/examples/Cards/MenuCards";
import {
  downloadGeneratedInvoice,
  getPendingPreInvoices,
  confirmPreInvoiceToInvoice,
} from "../api/invoiceApi";
import { ErrorAlert, InfoAlert } from "@shared/ui/alerts";
import { FormatNumberMoney } from "@shared/utils/moneyFormat";
import { ChargedInvoiceDialog } from "./ChargedInvoiceDialog";
import { sendInvoice } from "@modules/preinvoice";
import { usePermission } from "@shared/hooks/usePermission";
import { userContainPermission } from "@shared/utils/permission";
import { getFormatDate } from "@shared/utils/getFormatDate";

import type { PendingPreInvoice } from "../api/invoiceApi";

/** Campos de `userData` (hook legado `useUserProfile`, tipado `unknown`) que este componente lee. */
interface InvoiceItemUserData {
  firstName?: string;
  lastName?: string;
}

export interface InvoiceItemProps {
  thisLoading: () => void;
  notLoading: () => void;
  getListInvoice: () => void;
  userData: InvoiceItemUserData;
}

export const InvoiceItem = ({ thisLoading, notLoading, getListInvoice, userData }: InvoiceItemProps) => {
  const { t, i18n } = useTranslation();
  const [dialogManual, setDialogManual] = useState(false);
  const [dialogAutomatic, setDialogAutomatic] = useState(false);
  const { permissionList } = usePermission("Tipo Factura");
  const [subTypes, setSubTypes] = useState("Horas")
  const [titleDialogManual, setTitleDialogManual] = useState(t("manualInvoiceUploadByHour"))

  const onOpenDialog = (type: string) => {
    setSubTypes(type);
    if (type === "Administrativa") {
      setTitleDialogManual(t("administrativeManualInvoiceUpload"))
    } else {
      setTitleDialogManual(t("manualInvoiceUploadByHour"))
    }
    setDialogManual(true);
  }
  const onCloseDialog = () => {
    setDialogManual(false);
  }
  const onOpenDialogAutomatic = () => setDialogAutomatic(true);
  const onCloseDialogAutomatic = () => setDialogAutomatic(false);

  const existPermission = (permission: string): boolean => {
    return userContainPermission(permission, permissionList);
  };

  const onDownload = () => {
    thisLoading();
    downloadGeneratedInvoice()
      .then(({ data, headers }) => {
        const link = document.createElement("a");
        link.target = "_blank";
        link.download = `Factura_Inicial_${userData.firstName}_${userData.lastName}${getFormatDate()}.pdf`;
        link.href = window.URL.createObjectURL(
          new Blob([data], {
            type: headers["content-type"],
          })
        );
        link.click();
        notLoading();
      })
      .catch((error) => {
        const data = error.response?.data;
        console.log(data);
        notLoading();
        InfoAlert(
          t("notFoundInvoice"),
          t("unableToDownloadInvoice")
        ).then();
      });
  };

  const formatPeriod = (month: number, year: number) =>
    format(new Date(year, month - 1, 1), 'MMMM - yyyy', {
      locale: i18n.language === "es" ? es : enUS,
    }).toUpperCase();

  // `PendingPreInvoice` (PreInvoiceDTO) declara todos sus campos opcionales porque son
  // boxed en el backend; acá se castean tal como venía el código (comportamiento
  // preexistente, ver misma política en preinvoice/lib/invoiceFlow.ts).
  const confirmAndGeneratePreInvoice = (preInvoice: PendingPreInvoice) => {
    const periodLabel = formatPeriod(preInvoice.month as number, preInvoice.year as number);
    const totalLabel = FormatNumberMoney(preInvoice.currency as string).format(preInvoice.total as number);
    Swal.fire({
      title: t("generateInvoiceQuestion"),
      html: `<div>${t("period")}: <strong>${periodLabel}</strong></div>
             <div>${t("totalInvoice")}: <strong>${totalLabel}</strong></div>
             <br>${t("afterSendingNotModified")}`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: t("confirm"),
      cancelButtonText: "No",
    }).then(({ isConfirmed }) => {
      if (!isConfirmed) return;
      thisLoading();
      confirmPreInvoiceToInvoice(preInvoice.id as number)
        .then((data) => {
          notLoading();
          getListInvoice();
          sendInvoice(
            thisLoading,
            notLoading,
            data.id as number,
            "invoices/send",
            t,
            { period: periodLabel, total: totalLabel, onInvoiceSent: getListInvoice }
          ).then();
        })
        .catch(({ response }) => {
          notLoading();
          const errorData = response && response.data;
          if (errorData && errorData.code === 400) {
            ErrorAlert(t("errorToGenerateInvoiceTitle"), errorData.message).then();
            return;
          }
          ErrorAlert(t("errorTitle"), t("errorToGenerateInvoice")).then();
        });
    });
  };

  const onGenerate = () => {
    thisLoading();
    getPendingPreInvoices()
      .then((data) => {
        notLoading();
        if (!Array.isArray(data) || data.length === 0) {
          InfoAlert(
            t("generateInvoice"),
            t("noPreInvoicesAvailableToGenerateInvoice")
          ).then();
          return;
        }
        if (data.length > 1) {
          const periods = data
            .map((p) => formatPeriod(p.month as number, p.year as number))
            .join(", ");
          InfoAlert(
            t("generateInvoice"),
            t("multiplePreInvoicesPendingMessage", { count: data.length, periods })
          ).then();
          return;
        }
        // Los `if` de arriba dejan exactamente un elemento.
        confirmAndGeneratePreInvoice(data[0]!);
      })
      .catch((error) => {
        notLoading();
        const data = error.response?.data;
        if (data?.code === 400) {
          ErrorAlert(t("errorToGenerateInvoiceTitle"), data?.message ?? t("errorToGenerateInvoice")).then();
          return;
        }
        ErrorAlert(t("errorTitle"), t("errorToGenerateInvoice")).then();
      });
  };

  return (
    <Grid container spacing={3}>
      <Grid size={{ xs: 12, md: 6, lg: 4, xl: 4 }}>
        <MDBox mb={1.5} onClick={onGenerate}>
          <MenuCard icon={Cached} title={t("generate")} />
        </MDBox>
      </Grid>
      <Grid onClick={onDownload} size={{ xs: 12, md: 6, lg: 4, xl: 4 }}>
        <MDBox mb={1.5}>
          <MenuCard icon={Download} title={t("downloadInvoiceGenerate")} />
        </MDBox>
      </Grid>
      <Grid size={{ xs: 12, md: 6, lg: 4, xl: 4 }}>
        <MDBox mb={1.5}>
          <Link to="/gestion-de-facturas/facturas/ver">
            <MenuCard icon={Visibility} title={t("invoicesSent")} />
          </Link>
        </MDBox>
      </Grid>
      {existPermission("Factura Personal") && (
        <Grid onClick={() => onOpenDialog("Horas")} size={{ xs: 12, md: 6, lg: 4, xl: 4 }}>
          <MDBox mb={1.5}>
            <MenuCard icon={Upload} title={t("uploadManual")} />
          </MDBox>
        </Grid>
      )}
      {existPermission("Factura Personal Administrativa") && (
        <Grid onClick={() => onOpenDialog("Administrativa")} size={{ xs: 12, md: 6, lg: 4, xl: 4 }}>
          <MDBox mb={1.5}>
            <MenuCard icon={Upload} title={t("administrativeInvoiceUpload")} />
          </MDBox>
        </Grid>
      )}
      <ChargedInvoiceDialog
        notLoading={notLoading}
        loading={thisLoading}
        handleClose={onCloseDialog}
        open={dialogManual}
        title={titleDialogManual}
        subTypes={subTypes}
        t={t}
        onSuccess={() => {
            onCloseDialog();
            setTimeout(() => { getListInvoice(); }, 100);
         }}
      />
    </Grid>
  );
};
